import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { getYouTubePlayerState } from '@/utils/youtubeAPI';
import useYouTubePlayer from './hooks/useYouTubePlayer';
import useMediaSession from './hooks/useMediaSession';
import {
  play,
  pause,
  setCurrentVideo,
  updateProgress,
  setVolume as setVolumeAction,
  setMuted,
  playNext,
  playPrevious,
  setError,
  setLoading,
  selectCurrentVideo,
  selectIsPlaying,
  selectVolume,
  selectIsMuted,
  selectCurrentTime,
  selectDuration,
  type SerializableVideo,
} from './playerSlice';
import {
  enterDjMode,
  exitDjMode,
  loadDeck,
  setDeckPlaying,
  setDeckCue,
  setCrossfader,
  selectDjMode,
  selectCrossfader,
  selectDeckA,
  selectDeckB,
  selectAudibleDeck,
  type DeckId,
  type DeckState,
} from './decksSlice';
import { Video } from '@meradio/shared-types';

interface PlayerContextValue {
  currentVideo: SerializableVideo | null;
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  currentTime: number;
  duration: number;

  isPlayerReady: boolean;
  playerError: string | null;

  playVideo: (video: Video) => void;
  pauseVideo: () => void;
  seekTo: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  skipNext: () => void;
  skipPrevious: () => void;

  formatTime: (seconds: number) => string;
  getProgressPercentage: () => number;
}

interface DeckProgress {
  current: number;
  duration: number;
}

interface DecksContextValue {
  djMode: boolean;
  crossfader: number;
  decks: { a: DeckState; b: DeckState };
  deckProgress: { a: DeckProgress; b: DeckProgress };
  enter: () => void;
  exit: () => void;
  loadToDeck: (deck: DeckId, video: Video) => void;
  togglePlay: (deck: DeckId) => void;
  setCue: (deck: DeckId) => void;
  jumpToCue: (deck: DeckId) => void;
  seekDeck: (deck: DeckId, seconds: number) => void;
  setCrossfader: (value: number) => void;
}

const PlayerContext = createContext<PlayerContextValue | null>(null);
const DecksContext = createContext<DecksContextValue | null>(null);

const equalPowerVolume = (curve: 'cos' | 'sin', crossfader: number): number => {
  const angle = crossfader * (Math.PI / 2);
  const gain = curve === 'cos' ? Math.cos(angle) : Math.sin(angle);
  return Math.round(gain * 100);
};

interface PlayerProviderProps {
  children: React.ReactNode;
}

export const PlayerProvider: React.FC<PlayerProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();

  const currentVideo = useAppSelector(selectCurrentVideo);
  const isPlaying = useAppSelector(selectIsPlaying);
  const volume = useAppSelector(selectVolume);
  const isMuted = useAppSelector(selectIsMuted);
  const currentTime = useAppSelector(selectCurrentTime);
  const duration = useAppSelector(selectDuration);

  const djMode = useAppSelector(selectDjMode);
  const crossfader = useAppSelector(selectCrossfader);
  const deckAState = useAppSelector(selectDeckA);
  const deckBState = useAppSelector(selectDeckB);
  const audibleDeck = useAppSelector(selectAudibleDeck);

  const lastVideoIdRef = useRef<string | null>(null);
  const shouldAutoPlayRef = useRef<boolean>(false);
  const lastVideoIdRefB = useRef<string | null>(null);
  const shouldAutoPlayRefB = useRef<boolean>(false);

  const {
    isPlayerReady,
    error: playerError,
    play: youTubePlay,
    pause: youTubePause,
    seekTo: youTubeSeekTo,
    setVolume: youTubeSetVolume,
    mute: youTubeMute,
    unMute: youTubeUnMute,
    loadVideo: youTubeLoadVideo,
    getCurrentTime: youTubeGetCurrentTime,
  } = useYouTubePlayer({
    containerId: 'youtube-player',
    onReady: () => {
      dispatch(setError(null));
      if (shouldAutoPlayRef.current) {
        shouldAutoPlayRef.current = false;
        setTimeout(() => youTubePlay(), 100);
      }
    },
    onStateChange: (state) => {
      const playerStates = getYouTubePlayerState();

      if (state === playerStates.CUED && shouldAutoPlayRef.current) {
        shouldAutoPlayRef.current = false;
        youTubePlay();
        return;
      }

      switch (state) {
        case playerStates.PLAYING:
          shouldAutoPlayRef.current = false;
          dispatch(play());
          break;
        case playerStates.PAUSED:
          dispatch(pause());
          break;
        case playerStates.ENDED:
          dispatch(pause());
          dispatch(playNext());
          break;
        default:
          break;
      }
    },
    onError: (error) => {
      dispatch(setError(`Player error: ${error}`));
      dispatch(pause());
    },
    onProgress: (time, dur) => {
      dispatch(updateProgress({ currentTime: time, duration: dur }));
    },
  });

  const {
    isPlayerReady: isPlayerReadyB,
    currentTime: currentTimeB,
    duration: durationB,
    play: youTubePlayB,
    pause: youTubePauseB,
    seekTo: youTubeSeekToB,
    setVolume: youTubeSetVolumeB,
    loadVideo: youTubeLoadVideoB,
    getCurrentTime: youTubeGetCurrentTimeB,
    destroy: youTubeDestroyB,
  } = useYouTubePlayer({
    containerId: 'youtube-player-b',
    onReady: () => {
      if (shouldAutoPlayRefB.current) {
        shouldAutoPlayRefB.current = false;
        setTimeout(() => youTubePlayB(), 100);
      }
    },
    onStateChange: (state) => {
      const playerStates = getYouTubePlayerState();

      if (state === playerStates.CUED && shouldAutoPlayRefB.current) {
        shouldAutoPlayRefB.current = false;
        youTubePlayB();
        return;
      }

      switch (state) {
        case playerStates.PLAYING:
          shouldAutoPlayRefB.current = false;
          dispatch(setDeckPlaying({ deck: 'b', isPlaying: true }));
          break;
        case playerStates.PAUSED:
          dispatch(setDeckPlaying({ deck: 'b', isPlaying: false }));
          break;
        case playerStates.ENDED:
          dispatch(setDeckPlaying({ deck: 'b', isPlaying: false }));
          break;
        default:
          break;
      }
    },
  });

  const mediaVideo = djMode ? (audibleDeck === 'b' ? deckBState.video : currentVideo) : currentVideo;
  const mediaPlaying = djMode ? (audibleDeck === 'b' ? deckBState.isPlaying : isPlaying) : isPlaying;
  useMediaSession({ dispatch, currentVideo: mediaVideo, isPlaying: mediaPlaying });

  useEffect(() => {
    const wrapper = document.getElementById('youtube-player-wrapper');
    if (wrapper) wrapper.classList.toggle('is-active', Boolean(currentVideo));
  }, [currentVideo]);

  useEffect(() => {
    if (document.getElementById('youtube-player-wrapper')) return;

    const wrapperDiv = document.createElement('div');
    wrapperDiv.id = 'youtube-player-wrapper';
    wrapperDiv.className = 'youtube-player-wrapper';

    const playerDiv = document.createElement('div');
    playerDiv.id = 'youtube-player';
    playerDiv.style.width = '100%';
    playerDiv.style.height = '100%';
    wrapperDiv.appendChild(playerDiv);

    document.body.appendChild(wrapperDiv);

    return () => {
      wrapperDiv.remove();
    };
  }, []);

  useEffect(() => {
    if (!djMode) return;
    if (document.getElementById('youtube-player-wrapper-b')) return;

    const wrapperDiv = document.createElement('div');
    wrapperDiv.id = 'youtube-player-wrapper-b';
    wrapperDiv.className = 'youtube-player-wrapper deck-b is-active';

    const playerDiv = document.createElement('div');
    playerDiv.id = 'youtube-player-b';
    playerDiv.style.width = '100%';
    playerDiv.style.height = '100%';
    wrapperDiv.appendChild(playerDiv);

    document.body.appendChild(wrapperDiv);

    return () => {
      wrapperDiv.remove();
    };
  }, [djMode]);

  useEffect(() => {
    if (!djMode) {
      const wrapperA = document.getElementById('youtube-player-wrapper');
      if (wrapperA && wrapperA.parentElement !== document.body) {
        wrapperA.classList.remove('in-deck-slot');
        document.body.appendChild(wrapperA);
      }
      return;
    }

    // Moving an iframe reloads it, so the wrappers never change parents —
    // they stay fixed-position and get pinned over the deck slots by geometry
    const dock = () => {
      for (const [wrapperId, slotId] of [
        ['youtube-player-wrapper', 'deck-a-video-slot'],
        ['youtube-player-wrapper-b', 'deck-b-video-slot'],
      ]) {
        const wrapper = document.getElementById(wrapperId);
        const slot = document.getElementById(slotId);
        if (!wrapper) continue;
        if (slot) {
          const rect = slot.getBoundingClientRect();
          wrapper.classList.add('in-deck-slot');
          wrapper.style.top = `${rect.top}px`;
          wrapper.style.left = `${rect.left}px`;
          wrapper.style.width = `${rect.width}px`;
          wrapper.style.height = `${rect.height}px`;
          wrapper.style.bottom = 'auto';
          wrapper.style.right = 'auto';
        }
      }
    };

    dock();
    const interval = setInterval(dock, 200);
    window.addEventListener('resize', dock);
    window.addEventListener('scroll', dock, true);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', dock);
      window.removeEventListener('scroll', dock, true);
      for (const wrapperId of ['youtube-player-wrapper', 'youtube-player-wrapper-b']) {
        const wrapper = document.getElementById(wrapperId);
        if (wrapper) {
          wrapper.classList.remove('in-deck-slot');
          wrapper.style.top = '';
          wrapper.style.left = '';
          wrapper.style.width = '';
          wrapper.style.height = '';
          wrapper.style.bottom = '';
          wrapper.style.right = '';
        }
      }
    };
  }, [djMode]);

  useEffect(() => {
    if (currentVideo && currentVideo.videoId !== lastVideoIdRef.current) {
      dispatch(setLoading(true));
      shouldAutoPlayRef.current = isPlaying;
      youTubeLoadVideo(currentVideo.videoId);
      lastVideoIdRef.current = currentVideo.videoId;
    }
  }, [currentVideo, isPlaying, isPlayerReady, dispatch, youTubeLoadVideo]);

  useEffect(() => {
    if (!isPlayerReady || !currentVideo || shouldAutoPlayRef.current) return;
    if (currentVideo.videoId !== lastVideoIdRef.current) return;

    if (isPlaying) {
      youTubePlay();
    } else {
      youTubePause();
    }
  }, [isPlaying, isPlayerReady, currentVideo, youTubePlay, youTubePause]);

  useEffect(() => {
    if (!isPlayerReady || !currentVideo) return;
    if (currentVideo.videoId !== lastVideoIdRef.current) return;

    const vol = djMode ? equalPowerVolume('cos', crossfader) : (isMuted ? 0 : volume);
    youTubeSetVolume(vol);
  }, [volume, isMuted, isPlayerReady, currentVideo, youTubeSetVolume, djMode, crossfader]);

  useEffect(() => {
    if (!djMode) return;
    const video = deckBState.video;
    if (video && video.videoId !== lastVideoIdRefB.current) {
      shouldAutoPlayRefB.current = deckBState.isPlaying;
      youTubeLoadVideoB(video.videoId);
      lastVideoIdRefB.current = video.videoId;
    }
  }, [djMode, deckBState.video, deckBState.isPlaying, youTubeLoadVideoB]);

  useEffect(() => {
    if (!djMode || !isPlayerReadyB || !deckBState.video || shouldAutoPlayRefB.current) return;
    if (deckBState.video.videoId !== lastVideoIdRefB.current) return;

    if (deckBState.isPlaying) {
      youTubePlayB();
    } else {
      youTubePauseB();
    }
  }, [djMode, isPlayerReadyB, deckBState.video, deckBState.isPlaying, youTubePlayB, youTubePauseB]);

  useEffect(() => {
    if (!djMode || !isPlayerReadyB || !deckBState.video) return;
    if (deckBState.video.videoId !== lastVideoIdRefB.current) return;

    youTubeSetVolumeB(equalPowerVolume('sin', crossfader));
  }, [djMode, crossfader, isPlayerReadyB, deckBState.video, youTubeSetVolumeB]);

  const playVideo = (video: Video) => {
    dispatch(setCurrentVideo(video));
    dispatch(play());
  };

  const pauseVideo = () => {
    dispatch(pause());
  };

  const seekTo = (time: number) => {
    if (isPlayerReady) {
      youTubeSeekTo(time);
      dispatch(updateProgress({ currentTime: time, duration }));
    }
  };

  const setVolume = (newVolume: number) => {
    dispatch(setVolumeAction(newVolume));
  };

  const toggleMute = () => {
    const newMutedState = !isMuted;
    dispatch(setMuted(newMutedState));

    if (isPlayerReady) {
      if (newMutedState) {
        youTubeMute();
      } else {
        youTubeUnMute();
      }
    }
  };

  const skipNext = () => {
    dispatch(playNext());
  };

  const skipPrevious = () => {
    dispatch(playPrevious());
  };

  const formatTime = (seconds: number): string => {
    if (!seconds || seconds < 0) return '0:00';

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = (): number => {
    if (!duration || duration <= 0) return 0;
    return Math.min(100, (currentTime / duration) * 100);
  };

  const contextValue: PlayerContextValue = {
    currentVideo,
    isPlaying,
    volume,
    isMuted,
    currentTime,
    duration,

    isPlayerReady,
    playerError,

    playVideo,
    pauseVideo,
    seekTo,
    setVolume,
    toggleMute,
    skipNext,
    skipPrevious,

    formatTime,
    getProgressPercentage,
  };

  const enter = () => {
    dispatch(enterDjMode(currentVideo));
  };

  const exit = () => {
    youTubeDestroyB();
    lastVideoIdRefB.current = null;
    shouldAutoPlayRefB.current = false;
    dispatch(exitDjMode());
  };

  const loadToDeck = (deck: DeckId, video: Video) => {
    if (deck === 'a') {
      dispatch(pause());
      dispatch(setCurrentVideo(video));
      dispatch(setDeckCue({ deck: 'a', cuePoint: null }));
    } else {
      dispatch(loadDeck({ deck: 'b', video }));
    }
  };

  const togglePlay = (deck: DeckId) => {
    if (deck === 'a') {
      dispatch(isPlaying ? pause() : play());
    } else {
      dispatch(setDeckPlaying({ deck: 'b', isPlaying: !deckBState.isPlaying }));
    }
  };

  const seekDeck = (deck: DeckId, seconds: number) => {
    if (deck === 'a') {
      youTubeSeekTo(seconds);
      dispatch(updateProgress({ currentTime: seconds, duration }));
    } else {
      youTubeSeekToB(seconds);
    }
  };

  const setCue = (deck: DeckId) => {
    const cuePoint = deck === 'a' ? youTubeGetCurrentTime() : youTubeGetCurrentTimeB();
    dispatch(setDeckCue({ deck, cuePoint }));
  };

  const jumpToCue = (deck: DeckId) => {
    const cuePoint = deck === 'a' ? deckAState.cuePoint : deckBState.cuePoint;
    if (cuePoint != null) seekDeck(deck, cuePoint);
  };

  const handleSetCrossfader = (value: number) => {
    dispatch(setCrossfader(value));
  };

  const decksValue: DecksContextValue = {
    djMode,
    crossfader,
    decks: {
      a: { video: currentVideo, isPlaying, cuePoint: deckAState.cuePoint },
      b: deckBState,
    },
    deckProgress: {
      a: { current: currentTime, duration },
      b: { current: currentTimeB, duration: durationB },
    },
    enter,
    exit,
    loadToDeck,
    togglePlay,
    setCue,
    jumpToCue,
    seekDeck,
    setCrossfader: handleSetCrossfader,
  };

  return (
    <PlayerContext.Provider value={contextValue}>
      <DecksContext.Provider value={decksValue}>
        {children}
      </DecksContext.Provider>
    </PlayerContext.Provider>
  );
};

export const usePlayer = (): PlayerContextValue => {
  const context = useContext(PlayerContext);

  if (!context) {
    throw new Error('usePlayer must be used within PlayerProvider');
  }

  return context;
};

export const useDecks = (): DecksContextValue => {
  const context = useContext(DecksContext);

  if (!context) {
    throw new Error('useDecks must be used within PlayerProvider');
  }

  return context;
};

export default PlayerProvider;
